import { createAxios } from '../lib/axiosFactory';
import { errorParser } from '../lib/response/errorParser';
import { ActionResponse } from '../lib/interfaces/actionResponse';
import {
  ChargeBeeHostedPage,
  CompleteCheckoutRequestPayload,
  CompleteCheckoutResponsePayload,
  CreateCheckoutRequestPayload,
  CreateCheckoutResponsePayload,
  SubscriptionResponsePayload,
  PaymentSourceResponsePayload
} from '../lib/interfaces/subscription';
import { BillableItem } from '../lib/interfaces/billableItem';
import {
  BillableItemIds,
  BillableItemType,
  FeatureType
} from '../lib/enums/billableItem';
import moment from 'moment';
import { SystemContactLimit } from '../lib/resources/billableItems';
import { pick } from 'lodash';

const TelkomPromoPlanId = [BillableItemIds.TelkomPlan.TelkomFreemium];
const LowestTelkomPlanId = [
  BillableItemIds.TelkomPlan.TelkomStarterMonthly,
  ...TelkomPromoPlanId
];
const LowestUSPlanId = [
  BillableItemIds.Plan.StarterPlanUsdMonthly,
  BillableItemIds.Plan.StarterPlanUsdYearly
];

const Chargebee: any = window.Chargebee;

export const state = () => ({
  plans: [],
  addons: [],
  charges: [],
  numberLookups: [],
  subscription: null,
  contactLimit: null,
  smsLimit: null,
  totalNumberOfContacts: null,
  appSumoInvoiceId: null,
  topUpCreditCost: null
});

export const mutations = {
  setPlans(state: any, plans: BillableItem[]): void {
    state.plans = plans;
  },
  setAddons(state: any, addons: BillableItem[]): void {
    state.addons = addons;
  },
  setCharges(state: any, charges: BillableItem[]): void {
    state.charges = charges;
  },
  setSubscription(state: any, subscription: any): void {
    state.subscription = subscription;

    this.commit(
      'client/setSubscriptionStatus',
      {
        subscriptionStatus: subscription.status
      },
      { root: true }
    );
    this.commit(
      'client/setSubscriptionTrialEndAt',
      {
        subscriptionTrialEndAt: subscription.trialEndsAt
      },
      { root: true }
    );
    this.commit(
      'client/setSubscriptionPlanId',
      {
        subscriptionPlanId: subscription.subscriptionItems.find(
          item =>
            item.billableItemType === BillableItemType.Plan ||
            item.billableItemType === BillableItemType.TelkomPlan ||
            item.billableItemType === BillableItemType.AppSumoPlan
        )?.billableItemId
      },
      { root: true }
    );
    this.commit(
      'client/setSubscriptionSmsCreditsQuantity',
      {
        subscriptionSmsCreditsQuantity: subscription.subscriptionItems?.find(
          item =>
            item.billableItemType === BillableItemType.Addon &&
            Object.values(BillableItemIds.Addon).includes(item.billableItemId)
        )?.quantity
      },
      { root: true }
    );
    this.commit(
      'client/setSubscriptionContactLimit',
      {
        subscriptionContactLimit: subscription.contactLimit
      },
      { root: true }
    );
  },
  resetState(state: any): void {
    Object.assign(state, { ...state, subscription: null });
  },

  setContactLimit(state: any, contactLimit: number): void {
    state.contactLimit = contactLimit;
  },

  setTotalNumberOfContacts(state: any, totalNumberOfContacts: number): void {
    state.totalNumberOfContacts = totalNumberOfContacts;
  },

  setSmsLimit(state: any, smsLimit: number): void {
    state.smsLimit = smsLimit;
  },

  setAppSumoInvoiceId(state: any, appSumoInvoiceId: string): void {
    state.appSumoInvoiceId = appSumoInvoiceId;
  },

  setTopUpCreditCost(state: any, topUpCreditCost: number): void {
    state.topUpCreditCost = topUpCreditCost;
  }
};

export const getters = {
  getContactLimit: (state: any): string => {
    return state.contactLimit;
  },

  getSmsLimit: (state: any): string => {
    return state.smsLimit;
  },

  getTotalNumberOfContacts: (state: any): number => {
    return state.totalNumberOfContacts;
  },

  lowestPlanId: (
    state: any,
    // eslint-disable-next-line
    getters: any,
    rootState,
    rootGetters
  ): any[] => {
    return rootGetters['client/isTelkomClient']
      ? LowestTelkomPlanId
      : LowestUSPlanId;
  },

  trialEndAt: (
    state: any,
    // eslint-disable-next-line
    getters: any,
    rootState,
    rootGetters
  ): any => {
    return new Date(rootGetters['client/subscriptionTrialEndAt'] * 1000);
  },

  timeLeftForTrialDescription: (
    state: any,
    // eslint-disable-next-line
    getters: any
  ): any => {
    /**
     * moment.fromNow can rounds a day up or down.
     * We want to force it to ROUND UP ONLY
     *
     * Since we are (unintentionally) using Day-based billing in Chargebee production, when a user signs up for a 14 days trial, it might only have 13.2 days left.
     * e.g.
        the user that Ash noticed sees that there are only 13 days left instead of 14
        it signed up at 19:00 (7 pm)
        since it's using day-based proration, Chargebee assumes that the trial starts at 12 am of the day.
        there are only 13.2 days of trial left, so "13 days left" is shown in the UI
     * This changes forced the day number to only round up so that 13.2 would become 14.
     */
    const trialEndAt = getters.trialEndAt;
    const daysLeft = moment(trialEndAt).diff(new Date(), 'day', true);
    const hasMoreThan1DayLeft = daysLeft > 1;
    if (hasMoreThan1DayLeft) {
      const dayRoundedUp = Math.ceil(daysLeft);
      return `${dayRoundedUp} days`;
    }

    return moment(trialEndAt).fromNow(true);
  },

  isFreemiumTier: (
    state: any,
    getters: any,
    rootState,
    rootGetters
  ): boolean => {
    return TelkomPromoPlanId.includes(rootState.client.subscriptionPlanId);
  },

  isLifetimeTier: (
    state: any,
    getters: any,
    rootState,
    rootGetters
  ): boolean => {
    return Object.values(BillableItemIds.AppSumoPlan).includes(
      rootState.client.subscriptionPlanId
    );
  },

  isUnlimitedContactLimit(state): boolean {
    return state.contactLimit === SystemContactLimit;
  },

  nextBillingAt(state): number {
    return state.subscription?.nextBillingAt;
  },

  cancelledAt(state): number {
    return state.subscription?.cancelledAt;
  }
};

export const actions = {
  async loadPlans({ commit }): Promise<ActionResponse> {
    try {
      const billableItemsImport = await import(
        `../lib/resources/billableItems`
      );
      const { BillableItems } = billableItemsImport as {
        BillableItems: BillableItem[];
      };
      const plans = BillableItems.filter(item => {
        return [
          BillableItemType.Plan,
          BillableItemType.TelkomPlan,
          BillableItemType.AppSumoPlan
        ].includes(item.type);
      });
      commit('setPlans', plans);

      return { success: true, errors: {}, body: plans };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },
  async loadAddons({ commit }): Promise<ActionResponse> {
    try {
      const billableItemsImport = await import(
        `../lib/resources/billableItems`
      );
      const { BillableItems } = billableItemsImport as {
        BillableItems: BillableItem[];
      };
      const addons = BillableItems.filter(
        item => item.type === BillableItemType.Addon
      );
      commit('setAddons', addons);

      return { success: true, errors: {}, body: addons };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },
  async loadCharges({ commit }): Promise<ActionResponse> {
    try {
      const billableItemsImport = await import(
        `../lib/resources/billableItems`
      );
      const { BillableItems } = billableItemsImport as {
        BillableItems: BillableItem[];
      };
      const charges = BillableItems.filter(
        item => item.type === BillableItemType.Charge
      );
      commit('setCharges', charges);

      return { success: true, errors: {}, body: charges };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async createCheckout(
    { commit },
    payload: CreateCheckoutRequestPayload
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios().post<{
        data: CreateCheckoutResponsePayload;
      }>('/backend/checkouts-create', {
        data: {
          type: 'checkouts',
          attributes: payload
        }
      });

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async changeSubscription(
    { commit },
    payload: CreateCheckoutRequestPayload
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios().patch<{
        data: CreateCheckoutResponsePayload;
      }>('/backend/subscriptions-update', {
        data: {
          type: 'subscriptions',
          attributes: payload
        }
      });

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async completeCheckout(
    { commit },
    payload: CompleteCheckoutRequestPayload
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios().post<{
        data: CompleteCheckoutResponsePayload;
      }>('/backend/checkout-completes-create', {
        data: {
          type: 'checkouts',
          attributes: payload
        }
      });

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async getManagePaymentsHostedPage({ commit }): Promise<ActionResponse> {
    try {
      const response = await createAxios().post<{
        data: CompleteCheckoutResponsePayload;
      }>('/backend/chargebee-manage-payments-create', {
        data: {
          type: 'checkouts',
          attributes: {}
        }
      });
      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async getMySubscription({ commit }): Promise<ActionResponse> {
    try {
      const response = await createAxios().get<{
        data: SubscriptionResponsePayload;
      }>('/backend/subscriptions-show');
      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async loadCardInfo({ commit }): Promise<ActionResponse> {
    try {
      const response = await createAxios().get<{
        data: PaymentSourceResponsePayload;
      }>('/backend/payment-sources-show');

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async loadMySubscription({ commit }): Promise<ActionResponse> {
    try {
      const response = await createAxios().get<{
        data: SubscriptionResponsePayload;
      }>('/backend/subscriptions-show');

      commit('setSubscription', {
        ...pick(response.data.data, 'id'),
        ...response.data.data.attributes
      });

      commit('setContactLimit', response.data.data.attributes.contactLimit);
      commit(
        'setTotalNumberOfContacts',
        response.data.data.attributes.totalNumberOfContacts
      );
      commit('setSmsLimit', response.data.data.attributes.smsLimit);
      commit(
        'setAppSumoInvoiceId',
        response.data.data.attributes.appSumoInvoiceId
      );
      commit(
        'setTopUpCreditCost',
        response.data.data.attributes.topUpCreditCost
      );

      commit(
        'client/setSubscriptionStatus',
        {
          subscriptionStatus: response.data.data.attributes.status
        },
        { root: true }
      );
      commit(
        'client/setSubscriptionTrialEndAt',
        {
          subscriptionTrialEndAt: response.data.data.attributes.trialEndsAt
        },
        { root: true }
      );

      const subscriptionPlanId = response.data.data.attributes.subscriptionItems.find(
        item =>
          item.billableItemType === BillableItemType.Plan ||
          item.billableItemType === BillableItemType.TelkomPlan ||
          item.billableItemType === BillableItemType.AppSumoPlan
      )?.billableItemId;
      commit(
        'client/setSubscriptionPlanId',
        {
          subscriptionPlanId: subscriptionPlanId
        },
        { root: true }
      );

      commit(
        'client/setSubscriptionSmsCreditsQuantity',
        {
          subscriptionSmsCreditsQuantity: response.data.data.attributes.subscriptionItems?.find(
            item =>
              item.billableItemType === BillableItemType.Addon &&
              Object.values<string>(BillableItemIds.Addon).includes(
                item.billableItemId
              )
          )?.quantity
        },
        { root: true }
      );

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  initChargeBee(): void {
    Chargebee.init({ site: process.env.ChargeBeeSiteId });
  },

  async openChargebeeCheckOut(
    { commit },
    hostedPage: ChargeBeeHostedPage
  ): Promise<{ isCancel?: boolean } & ActionResponse> {
    const chargebeeInstance = Chargebee.getInstance();
    let success = false;

    return new Promise(resolve => {
      try {
        chargebeeInstance.openCheckout({
          hostedPage: async () => {
            return hostedPage;
          },

          // this is supposed to work, but doesn't
          success: () => {
            success = true;
            resolve({ success: true, errors: {}, body: hostedPage });
          },

          close: () => {
            resolve({
              success,
              errors: {},
              body: hostedPage,
              isCancel: true
            });
          },

          step: step => {
            if (step === 'thankyou_screen') success = true;
          },

          error: error => {
            chargebeeInstance.closeAll();
            resolve({
              success: false,
              errors: errorParser(error),
              body: error
            });
          }
        });
      } catch (error) {
        chargebeeInstance.closeAll();
        resolve({
          success: false,
          errors: errorParser(error),
          body: error
        });
      }
    });
  },

  async cancelSubscription(
    { commit },
    { cancelReason, cancelComments }
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios().post(
        '/backend/subscription-cancellations-create',
        {
          data: {
            type: 'subscriptions',
            attributes: {
              cancelReason,
              cancelComments
            }
          }
        }
      );
      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  }
};
